/**
 * Base path for every tag mapping admin endpoint, mounted under `/admin/tags`
 * on the backend. The same endpoints serve the product-wide screen and each
 * product line's own sub-page; a sub-page only pins its type.
 */
export const TAGS_API_BASE = "admin/tags";

/** Path (relative to `TAGS_API_BASE`) for the list of canonical tags to pick from. */
export const CANONICAL_TAGS_ENDPOINT_PATH = "canonical-tags";

/** Path (relative to `TAGS_API_BASE`) for the fields one product line adds to a mapping. */
export const METADATA_FIELDS_ENDPOINT_PATH = "metadata-fields";

export const TAGS_PAGINATION_CONFIG = Object.freeze({
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: ["10", "25", "50", "100"]
});

/**
 * Mirrors the backend's `TAG_VOCABULARY_DOMAINS`
 * (`backend-service/src/lib/model-utils/enums/features/tag-vocabulary.js`).
 * One value per product line that keeps its own tag mappings.
 */
export const TAG_DOMAIN_ENUM = Object.freeze({
  EXTERNAL_PROBLEM: "EXTERNAL_PROBLEM"
});

/** Label an operator reads for each product line, never the stored value. */
export const TAG_DOMAIN_DISPLAY_NAME_MAP = Object.freeze({
  [TAG_DOMAIN_ENUM.EXTERNAL_PROBLEM]: "External problems"
});

export const TAG_DOMAIN_FILTER_OPTIONS = Object.values(TAG_DOMAIN_ENUM).map((tagDomain) => ({
  label: TAG_DOMAIN_DISPLAY_NAME_MAP[tagDomain],
  value: tagDomain
}));

/**
 * The review states a mapping moves through, as the tab strip above the table.
 * `isReviewed` is the param each tab sends; `undefined` sends none at all,
 * which is how "All" differs from either single state.
 */
export const REVIEW_STATE_TABS = Object.freeze([
  {
    key: "AWAITING_REVIEW",
    label: "Awaiting review",
    isReviewed: "false",
    description:
      "Mappings nobody has decided on yet. A source used this tag, and which of our tags it means is still open.",
    emptyText: "Every mapping is reviewed. Nothing is waiting for a decision."
  },
  {
    key: "REVIEWED",
    label: "Reviewed",
    isReviewed: "true",
    description:
      "Mappings an admin has decided. That includes deciding a source's tag has no equivalent of ours, which is why a reviewed row can still show an empty canonical tag.",
    emptyText: "No mapping has been reviewed yet."
  },
  {
    key: "ALL",
    label: "All",
    isReviewed: undefined,
    description: "Every mapping in this list, whichever state it is in.",
    emptyText: "No mappings yet. Use Add mapping to create one."
  }
]);

/** One sentence of plain English per column and control, shown behind the info icon beside it. */
export const TAG_SCREEN_HELP_TEXT = Object.freeze({
  tagDomain:
    "The product line this mapping belongs to. Each keeps its own set of canonical tags, so the same word can mean different things in two of them.",
  sourceTag:
    "The tag exactly as the source publishes it. Empty on a canonical tag an admin added, which comes from no source.",
  canonicalTag:
    "Our own tag for the same idea, shared across every source in this product line. Saving this empty records that the source's tag has no equivalent of ours, which is a decision rather than an unfinished row.",
  isReviewed:
    "Whether an admin has decided what this source tag maps to. A mapping nobody has reviewed is not the same as one reviewed as having no equivalent.",
  metadata:
    "The extra fields this product line records against a mapping. Each product line defines its own, so the form is built from whatever the chosen type declares."
});

/** Explains what the create form does, on both the product-wide screen and a sub-page. */
export const CREATE_MAPPING_INFO_TEXT =
  "Adds a mapping by hand: the source's own tag on one side, our tag on the other. Leave the source tag empty to add a canonical tag on its own, for an idea no source has published a tag for yet. A source tag already mapped in this product line is refused rather than duplicated.";

/** Colour of the platform chip in the metadata column, one per judge the catalogue stores. */
export const PLATFORM_TAG_COLOR_MAP = Object.freeze({
  CODEFORCES_PROBLEM: "blue",
  LEETCODE_PROBLEM: "orange",
  ATCODER_PROBLEM: "geekblue",
  CODECHEF_PROBLEM: "volcano",
  HACKEREARTH_PROBLEM: "purple",
  CSES_PROBLEM: "green",
  SPOJ_PROBLEM: "cyan",
  GFG_PROBLEM: "lime",
  HACKERRANK_PROBLEM: "magenta"
});

/** Colour of the platform chip for a platform the map does not list. */
export const FALLBACK_PLATFORM_TAG_COLOR = "default";

/** Metadata key whose value is a platform and renders as a coloured chip. */
export const PLATFORM_METADATA_KEY = "platform";
